import { useEffect, useId, useMemo, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { CircleAlert, FileText, Search, Star, Users, Workflow, X } from "lucide-react"
import { searchWorkspaceItems } from "../lib/workspaceSearch"

const itemTypes = {
  concept: { label: "Concept", Icon: Star },
  persona: { label: "Persona", Icon: Users },
  action: { label: "Action", Icon: Workflow },
  issue: { label: "Issue", Icon: CircleAlert },
  context: { label: "Document", Icon: FileText }
}

export function WorkspaceSearch({ items, onClose, onSelect }) {
  const [query, setQuery] = useState("")
  const [activeIndex, setActiveIndex] = useState(0)
  const dialogRef = useRef(null)
  const inputRef = useRef(null)
  const listId = useId()
  const results = useMemo(() => searchWorkspaceItems(items, query), [items, query])
  const selectedIndex = Math.min(activeIndex, Math.max(0, results.length - 1))

  useEffect(() => {
    const previousFocus = document.activeElement
    const previousOverflow = document.body.style.overflow
    const dialog = dialogRef.current
    dialog.showModal()
    document.body.style.overflow = "hidden"
    inputRef.current?.focus()
    return () => {
      dialog.close()
      document.body.style.overflow = previousOverflow
      if (previousFocus?.isConnected) previousFocus.focus()
    }
  }, [])

  useEffect(() => {
    document.getElementById(`${listId}-${selectedIndex}`)?.scrollIntoView({ block: "nearest" })
  }, [listId, selectedIndex, query])

  function handleKeyDown(event) {
    if ((event.key === "ArrowDown" || event.key === "ArrowUp") && results.length) {
      event.preventDefault()
      setActiveIndex((selectedIndex + (event.key === "ArrowDown" ? 1 : -1) + results.length) % results.length)
    } else if (event.key === "Enter" && results[selectedIndex]) {
      event.preventDefault()
      onSelect(results[selectedIndex])
    }
  }

  return createPortal(
    <dialog
      ref={dialogRef}
      className="workspace-search-dialog"
      aria-label="Search items"
      onCancel={(event) => { event.preventDefault(); onClose() }}
      onClick={(event) => { if (event.target === event.currentTarget) onClose() }}
    >
      <div className="workspace-search-surface">
        <div className="workspace-search-input-row">
          <Search aria-hidden="true" strokeWidth={1.7} />
          <input
            ref={inputRef}
            aria-label="Search concepts, personas, actions, issues, and context.md"
            aria-autocomplete="list"
            aria-controls={listId}
            aria-expanded={true}
            aria-activedescendant={results.length ? `${listId}-${selectedIndex}` : undefined}
            autoComplete="off"
            placeholder="Search items…"
            role="combobox"
            value={query}
            onChange={(event) => { setQuery(event.target.value); setActiveIndex(0) }}
            onKeyDown={handleKeyDown}
          />
          <button type="button" className="workspace-search-close" aria-label="Close search" onClick={onClose}>
            <X aria-hidden="true" />
          </button>
        </div>
        <div id={listId} className="workspace-search-results" role="listbox" aria-label="Search results">
          {results.map((result, index) => {
            const { label, Icon } = itemTypes[result.type]
            return (
              <a
                id={`${listId}-${index}`}
                key={result.id}
                className={`workspace-search-result${index === selectedIndex ? " is-selected" : ""}`}
                href={result.href}
                role="option"
                aria-selected={index === selectedIndex}
                tabIndex={-1}
                onClick={(event) => onSelect(result, event)}
                onMouseMove={() => setActiveIndex(index)}
              >
                <Icon aria-hidden="true" strokeWidth={1.7} />
                <span className="workspace-search-result-copy">
                  <span className="workspace-search-result-title">{result.title}</span>
                  {result.description ? <span className="workspace-search-result-description">{result.description}</span> : null}
                </span>
                <span className="workspace-search-result-type">{label}</span>
              </a>
            )
          })}
        </div>
        {!results.length ? (
          <p className="workspace-search-empty">
            {query.trim() ? "No matching items." : "Search concepts, personas, actions, issues, and context.md."}
          </p>
        ) : null}
        <div className="workspace-search-footer" role="status">
          {query.trim() ? `${results.length} ${results.length === 1 ? "result" : "results"}` : "Type to search"}
          <span>↑ ↓ to navigate · Enter to open</span>
        </div>
      </div>
    </dialog>, document.body
  )
}
